/**
 * @file 爆款裂变 store
 */

import {getUseridFromCokkie} from '@/common/util';

// 跨页面保留「解析中/已解析」状态用的 sessionStorage key。
// 按当前登录用户隔离：key 带上 userid，避免切换账号后读到上一账号残留的解析现场
const getPersistKey = () => `viralFissionState_${getUseridFromCokkie()}`;
// 需要持久化的字段：离开页面再回来时用它们恢复现场，其余字段（如 MAX_* 常量）无需持久化
const PERSIST_FIELDS = [
    'hotVideoParseResult',
    'hotVideo',
    'originVideoType',
    'digitalHumanList',
    'materialList',
    'prePasteList',
    'postSetting',
    'useOriginalAudio',
    'combineFission',
    'hotVideoTaskId',
    'parseStatus'
];

function loadPersistedState() {
    try {
        const raw = sessionStorage.getItem(getPersistKey());
        return raw ? JSON.parse(raw) : {};
    }
    catch (e) {
        return {};
    }
}

function persist(state) {
    try {
        const data = PERSIST_FIELDS.reduce((acc, key) => {
            acc[key] = state[key];
            return acc;
        }, {});
        sessionStorage.setItem(getPersistKey(), JSON.stringify(data));
    }
    catch (e) {
        // 忽略持久化失败（如隐私模式下 sessionStorage 不可用），不影响主流程
    }
}

function clearPersisted() {
    try {
        sessionStorage.removeItem(getPersistKey());
    }
    catch (e) {
        // 忽略
    }
}

const defaultState = () => ({
    // 解析后的视频数据
    hotVideoParseResult: {},
    // 原视频类型，1用户上传，2主体视频
    originVideoType: 1,
    // 爆款视频
    hotVideo: null,
    // 替换数字人列表；命中人物替换白名单时每项带 personId 标记所属人物
    digitalHumanList: [],
    // 分镜替换素材列表，每项带 shotIndex 标记所属分镜
    materialList: [],
    // 前贴素材列表
    prePasteList: [],
    // 后期设置
    postSetting: {
        caption: {}, // 字幕
        stickerList: [], // 贴纸
        textList: [], // 固定文案
        subtitleMode: 'smart' // 字幕是否智能推荐模式
    },
    // 数字人裂变最大数量：人物替换白名单内为单个人物上限，白名单外为全局上限
    MAX_DIGITAL_HUMAN_COUNT: 5,
    // 前贴素材裂变最大数量
    MAX_PRE_PASTE_COUNT: 5,
    // 单个分镜可替换素材最大数量
    MAX_SHOT_MATERIAL_COUNT: 3,
    // 是否使用原音频
    useOriginalAudio: true,
    // 是否组合裂变
    combineFission: false,
    // 爆款视频解析任务 id，用于离开页面后恢复轮询
    hotVideoTaskId: '',
    // 解析状态：idle 未开始 / parsing 解析中 / success 解析成功 / failed 解析失败
    parseStatus: 'idle'
});

// 初始 state 融合 sessionStorage 中离开前保存的现场，恢复"解析中/已解析"进度
const state = {
    ...defaultState(),
    ...loadPersistedState()
};

const mutations = {
    SET_HOT_VIDEO(state, video) {
        state.hotVideo = video;
        persist(state);
    },
    SET_DIGITAL_HUMAN_LIST(state, list) {
        state.digitalHumanList = list;
        persist(state);
    },
    SET_MATERIAL_LIST(state, list) {
        state.materialList = list;
        persist(state);
    },
    SET_PRE_PASTE_LIST(state, list) {
        state.prePasteList = list;
        persist(state);
    },
    SET_POST_SETTING(state, postSetting) {
        state.postSetting = postSetting;
        persist(state);
    },
    SET_FIELD(state, {key, value}) {
        state[key] = value;
        persist(state);
    },
    RESET_STATE(state) {
        Object.assign(state, defaultState());
        clearPersisted();
    }
};

const actions = {
    setHotVideo({commit}, video) {
        commit('SET_HOT_VIDEO', video);
    },
    setHotVideoParseResult({commit}, result) {
        commit('SET_FIELD', {key: 'hotVideoParseResult', value: result});
    },
    setOriginVideoType({commit}, value) {
        commit('SET_FIELD', {key: 'originVideoType', value});
    },
    setDigitalHumanList({commit}, list) {
        commit('SET_DIGITAL_HUMAN_LIST', list);
    },
    setMaterialList({commit}, list) {
        commit('SET_MATERIAL_LIST', list);
    },
    setPrePasteList({commit}, list) {
        commit('SET_PRE_PASTE_LIST', list);
    },
    setPostSetting({commit}, postSetting) {
        commit('SET_POST_SETTING', postSetting);
    },
    setUseOriginalAudio({commit}, value) {
        commit('SET_FIELD', {key: 'useOriginalAudio', value});
    },
    setCombineFission({commit}, value) {
        commit('SET_FIELD', {key: 'combineFission', value});
    },
    setHotVideoTaskId({commit}, value) {
        commit('SET_FIELD', {key: 'hotVideoTaskId', value});
    },
    setParseStatus({commit}, value) {
        commit('SET_FIELD', {key: 'parseStatus', value});
    },
    // 批量更新
    batchUpdate({commit}, payload) {
        Object.keys(payload).forEach(key => {
            commit('SET_FIELD', {key, value: payload[key]});
        });
    },
    // 提交任务成功后整体重置，回到初始空态
    resetState({commit}) {
        commit('RESET_STATE');
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions
};
